import { useState, useEffect, useCallback, useRef } from 'react';
import { Container, NavbarBrand, Navbar, ListGroup } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import dayjs from 'dayjs';
import ReactGA from 'react-ga4';
import '../css/livetimetable.css';
import { useCompetition } from './CompetitionContext';
import { useAuth } from './auth/AuthContext';
import { createRealtimeMapCoalescer, getConnectedUsersCount, subscribeToRealtime } from './realtimeHub';

export function Commentary() {
    const MAX_COMMENTARY_ENTRIES = 60;
    const SWIPE_DELETE_THRESHOLD = Math.round(window.innerWidth * 0.5);
    const SWIPE_MAX_DISTANCE = Math.round(window.innerWidth * 0.6);
    const [races, setRaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setStatus] = useState('Disconnected.');
    const [, setLastUpdate] = useState('');
    const [, setConnectedUsers] = useState(null);
    const [vl, setVL] = useState('Loading...');
    const [racesTG, setRacesTG] = useState(0);
    const [onCourseTickerText, setOnCourseTickerText] = useState('Course Empty');
    const [divisionBreakMinutes, setDivisionBreakMinutes] = useState(30);
    const [commentaryEntries, setCommentaryEntries] = useState([]);
    const [commentaryEntryAnimationStages, setCommentaryEntryAnimationStages] = useState({});
    const [commentaryHiddenStorageKey, setCommentaryHiddenStorageKey] = useState('commentary-hidden-default');
    const [commentarySwipe, setCommentarySwipe] = useState({ id: null, deltaX: 0 });
    const [isRaceDetailOpen, setIsRaceDetailOpen] = useState(false);
    const [raceDetailEntry, setRaceDetailEntry] = useState(null);
    const [raceDetailData, setRaceDetailData] = useState(null);
    const [raceDetailCrewMembers, setRaceDetailCrewMembers] = useState({ laneA: [], laneB: [] });
    const [isRaceDetailLoading, setIsRaceDetailLoading] = useState(false);
    const isMountedRef = useRef(false);
    const commentaryTouchRef = useRef({ id: null, startX: 0, startY: 0, currentX: 0, currentY: 0, swiped: false, axisLock: null });
    const commentarySwipeRafRef = useRef(null);
    const commentarySwipePendingRef = useRef({ id: null, deltaX: 0 });
    const hiddenCommentaryIdsRef = useRef(new Set());
    const commentaryAnimationTimeoutsRef = useRef(new Map());
    const raceDetailCrewMembersCacheRef = useRef(new Map());
    const { currentYear } = useCompetition();
    const { getToken, isAdmin, loading: authLoading } = useAuth();

    const getLaneBladeImages = useCallback((lane) => {
        const values = [];
        if (Array.isArray(lane?.bladeImages)) values.push(...lane.bladeImages);
        if (lane?.bladeImage) values.push(lane.bladeImage);
        return Array.from(new Set(values
            .map(value => String(value || '').trim())
            .filter(Boolean)));
    }, []);

    const renderLaneBlades = useCallback((lane) => {
        const bladeSources = getLaneBladeImages(lane);
        if (!bladeSources.length) return null;

        return (
            <span className="commentary-lane-blades" aria-hidden="true">
                {bladeSources.map((src, index) => (
                    <img
                        key={`${src}-${index}`}
                        className="commentary-lane-blade"
                        src={src}
                        alt=""
                        loading="lazy"
                        onError={(event) => {
                            event.currentTarget.style.display = 'none';
                        }}
                    />
                ))}
            </span>
        );
    }, [getLaneBladeImages]);

    const getRaceBadgeLabel = useCallback((round) => {
        const normalized = String(round || '').trim().toUpperCase();
        if (!normalized) return 'R?';
        if (normalized === 'FINAL') return 'F';
        if (normalized === 'SEMI FINAL') return 'SF';
        if (normalized === 'QUARTER FINAL') return 'QF';
        if (/^R\d+$/.test(normalized)) return normalized;
        if (['F', 'SF', 'QF'].includes(normalized)) return normalized;
        return normalized;
    }, []);

    const parseLegacyCommentaryText = useCallback((text) => {
        const normalized = String(text || '').trim();
        if (!normalized) return {};

        const startMatch = normalized.match(/^Race\s+\d+\s+([A-Z0-9.]+)\s+in\s+(.+?)\s+started between\s+(.+?)\s+and\s+(.+?)\.(.*)$/i);
        if (startMatch) {
            return {
                round: startMatch[1],
                subject: startMatch[2],
                preview: `${startMatch[3]} v ${startMatch[4]}${startMatch[5] ? startMatch[5] : ''}`.trim()
            };
        }

        const resultMatch = normalized.match(/^Race\s+\d+\s+([A-Z0-9.]+)\s+in\s+(.+?)\s+WIN for\s+(.+?)\s+over\s+(.+?)(\.|\sin\s+.*)$/i);
        if (resultMatch) {
            return {
                round: resultMatch[1],
                subject: resultMatch[2],
                preview: `${resultMatch[3]} won over ${resultMatch[4]}${resultMatch[5] ? resultMatch[5] : ''}`.trim()
            };
        }

        const breakMatch = normalized.match(/^We now break for\s+(.+?)\s+between divisions\.?$/i);
        if (breakMatch) {
            return {
                subject: 'Between divisions',
                preview: `We now break for ${breakMatch[1]} between divisions.`
            };
        }

        return { preview: normalized };
    }, []);

    const parseRoundAndEventFromText = useCallback((text) => {
        const normalized = String(text || '').trim();
        if (!normalized) return {};

        const startedMatch = normalized.match(/^Race\s+(\d+)\s+([A-Z0-9.]+)\s+in\s+(.+?)\s+started between\s+(.+?)\s+and\s+(.+?)\.?$/i);
        if (startedMatch) {
            return {
                raceNo: String(startedMatch[1] || '').trim(),
                round: String(startedMatch[2] || '').trim(),
                eventCode: String(startedMatch[3] || '').trim(),
                status: 'STARTED',
                crewA: String(startedMatch[4] || '').trim(),
                crewB: String(startedMatch[5] || '').trim()
            };
        }

        const resultMatch = normalized.match(/^Race\s+(\d+)\s+([A-Z0-9.]+)\s+in\s+(.+?)\s+WIN for\s+(.+?)\s+over\s+(.+?)(?:\.|\sin\s+.*)$/i);
        if (resultMatch) {
            return {
                raceNo: String(resultMatch[1] || '').trim(),
                round: String(resultMatch[2] || '').trim(),
                eventCode: String(resultMatch[3] || '').trim(),
                status: 'RESULT',
                crewA: String(resultMatch[4] || '').trim(),
                crewB: String(resultMatch[5] || '').trim()
            };
        }

        const waitingMatch = normalized.match(/^Race\s+(\d+)\s+([A-Z0-9.]+)\s+in\s+(.+?)\s+is\s+waiting\.?$/i);
        if (waitingMatch) {
            return {
                raceNo: String(waitingMatch[1] || '').trim(),
                round: String(waitingMatch[2] || '').trim(),
                eventCode: String(waitingMatch[3] || '').trim(),
                status: 'ALIGNING'
            };
        }

        const looseMatch = normalized.match(/Race\s+(\d+)\s+([A-Z0-9.]+)\s+in\s+(.+)/i);
        if (looseMatch) {
            return {
                raceNo: String(looseMatch[1] || '').trim(),
                round: String(looseMatch[2] || '').trim(),
                eventCode: String(looseMatch[3] || '').trim()
            };
        }

        return {};
    }, []);

    const getCrewLabel = useCallback((lane, isHandicapEvent) => {
        const club = String(lane?.titleAbbreviated || lane?.titleMobile || lane?.titleFull || lane?.title || lane?.titleShort || '').trim();
        const competitorNo = String(lane?.competitorNo || '').trim();
        const crew = String(lane?.suffix || '').trim();
        const mastersCategory = String(lane?.mastersCategory || '').trim();
        const clubWithNumber = competitorNo && !/^(TBC|BYE)$/i.test(club) && !club.startsWith(`${competitorNo} - `)
            ? `${competitorNo} - ${club}`
            : club;
        const base = crew ? `${clubWithNumber} (${crew})` : clubWithNumber;
        const withMasters = isHandicapEvent && mastersCategory ? `${base} [Mas${mastersCategory}]` : base;
        return withMasters || 'Unknown crew';
    }, []);

    const getCrewLabelWithBlades = useCallback((lane, isHandicapEvent) => {
        return (
            <span className="commentary-lane-with-blades">
                {renderLaneBlades(lane)}
                <span>{getCrewLabel(lane, isHandicapEvent)}</span>
            </span>
        );
    }, [getCrewLabel, renderLaneBlades]);

    const getEventCode = useCallback((race) => {
        const eventCode = String(race?.event?.titleShort || '').trim();
        if (eventCode) return eventCode;
        return String(race?.event?.title || '').trim() || 'Race';
    }, []);

    const buildRaceSubject = useCallback((race, statusWord) => {
        const round = getRaceBadgeLabel(race?.round);
        const eventCode = getEventCode(race);
        const status = String(statusWord || '').trim().toUpperCase();
        return `${round} ${eventCode}${status ? ` ${status}` : ''}`.trim();
    }, [getEventCode, getRaceBadgeLabel]);

    const buildRaceBody = useCallback((race) => {
        const isHandicapEvent = Boolean(race?.event?.handicapEvent);
        return `${getCrewLabel(race?.laneA, isHandicapEvent)} v. ${getCrewLabel(race?.laneB, isHandicapEvent)}`;
    }, [getCrewLabel]);

    const getEventGroup = useCallback((entry) => {
        const payloadGroup = String(entry?.payload?.eventGroup || entry?.eventGroup || '').trim().toLowerCase();
        if (['senior', 'master', 'junior'].includes(payloadGroup)) return payloadGroup;

        const text = `${String(entry?.subject || '')} ${String(entry?.eventCode || '')}`.toLowerCase();
        if (/\bmas\b|master/.test(text)) return 'master';
        if (/\bj\d{1,2}\b|junior/.test(text)) return 'junior';
        return 'senior';
    }, []);

    const getStatusTone = useCallback((entry) => {
        const status = String(entry?.status || '').trim().toUpperCase();
        const kind = String(entry?.kind || '').trim().toLowerCase();

        if (status === 'ALIGNING' || kind === 'next') return 'aligning';
        if (status === 'STOPPED') return 'stopped';
        if (['RESULT', 'FINISHED', 'FINISH', 'COMPLETED'].includes(status) || kind === 'result') return 'finished';
        if (['STARTED', 'RACING'].includes(status) || kind === 'start') return 'racing';
        return 'neutral';
    }, []);

    const formatBreakMinutes = useCallback((minutes) => {
        const parsed = Number(minutes);
        if (!Number.isFinite(parsed) || parsed <= 0) return '30 minutes';
        return `${parsed} minute${parsed === 1 ? '' : 's'}`;
    }, []);

    const parseRaceTimeSeconds = useCallback((value) => {
        const text = String(value || '').trim().toLowerCase();
        if (!text || text === 'ntt') return null;

        const parts = text.split(':').map(part => Number(part));
        if (parts.some(part => Number.isNaN(part))) return null;
        if (parts.length === 1) return parts[0];
        if (parts.length === 2) return (parts[0] * 60) + parts[1];
        if (parts.length === 3) return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
        return null;
    }, []);

    const makeCommentaryId = useCallback((entry, index = 0) => {
        const racePart = String(entry?.raceId ?? '').trim();
        // Prefer the full seconds-precision timestamp so two state changes
        // on the same race in the same minute don't share an ID.
        const timePart = String(entry?.timestamp || entry?.time || '').trim();
        const kindPart = String(entry?.kind || 'info').trim();
        return `${racePart}|${timePart}|${kindPart}|${index}`;
    }, []);

    const normalizeCommentaryEntry = useCallback((entry, index = 0) => {
        if (!entry) return null;
        const now = dayjs();
        const time = String(entry.time || '').trim() || now.format('HH:mm');
        const timestamp = String(entry.timestamp || '').trim() || now.format('HH:mm:ss');
        const kind = String(entry.kind || 'info').trim() || 'info';
        const legacy = !entry.subject || !entry.preview ? parseLegacyCommentaryText(entry.text) : {};
        const fallbackFromText = parseRoundAndEventFromText(entry.preview || entry.text || '');
        const round = String(entry.round || entry.raceRound || legacy.round || fallbackFromText.round || '').trim();
        const eventCode = String(entry.eventCode || fallbackFromText.eventCode || '').trim();
        const status = String(entry.status || fallbackFromText.status || '').trim().toUpperCase();

        let subject = String(entry.subject || entry.eventTitle || entry.raceTitle || legacy.subject || '').trim();
        if (!subject || subject.toLowerCase() === 'race update') {
            const subjectParts = [round || getRaceBadgeLabel(round), eventCode || '', status || ''];
            subject = subjectParts.filter(Boolean).join(' ').trim() || 'Race update';
        }

        let preview = String(entry.preview || legacy.preview || entry.text || '').trim();
        const parsedCrewA = String(entry.crewA || fallbackFromText.crewA || '').trim();
        const parsedCrewB = String(entry.crewB || fallbackFromText.crewB || '').trim();
        if (parsedCrewA && parsedCrewB) {
            preview = `${parsedCrewA} v. ${parsedCrewB}`;
        }

        const raceNo = String(entry.raceNo || fallbackFromText.raceNo || '').trim();
        const icon = String(entry.icon || raceNo || getRaceBadgeLabel(round)).trim() || 'R?';
        const payload = entry?.payload && typeof entry.payload === 'object' ? entry.payload : {};
        const resultLine = String(entry.resultLine || payload.resultLine || '').trim();
        const winningTime = String(entry.winningTime || payload.winningTime || '').trim();
        const verdict = String(entry.verdict || payload.verdict || '').trim();
        const eventResultCount = Number(entry.eventResultCount ?? payload.eventResultCount ?? 0);
        const isEventFastest = Boolean(entry.isEventFastest ?? payload.isEventFastest);
        const eventGroup = getEventGroup({ ...entry, eventCode, subject, payload });
        const statusTone = getStatusTone({ ...entry, status, kind });
        const normalizedEntry = {
            id: entry.id || makeCommentaryId({ ...entry, time, timestamp }, index),
            raceId: entry.raceId ?? null,
            time,
            timestamp,
            kind,
            round,
            raceNo,
            eventCode,
            status,
            crewA: parsedCrewA,
            crewB: parsedCrewB,
            subject,
            preview,
            resultLine,
            winningTime,
            verdict,
            eventResultCount,
            isEventFastest,
            eventGroup,
            statusTone,
            icon,
            text: entry.text || preview
        };
        // Ensure id is always derived from the final fields
        if (!entry.id) normalizedEntry.id = makeCommentaryId(normalizedEntry, index);
        return normalizedEntry;
    }, [getEventGroup, getRaceBadgeLabel, getStatusTone, makeCommentaryId, parseLegacyCommentaryText, parseRoundAndEventFromText]);

    const persistHiddenCommentaryIds = useCallback((idsSet) => {
        try {
            window.localStorage.setItem(commentaryHiddenStorageKey, JSON.stringify([...idsSet]));
        } catch (_error) {
            // Ignore storage write errors in private browsing modes.
        }
    }, [commentaryHiddenStorageKey]);

    const loadHiddenCommentaryIds = useCallback(() => {
        try {
            const storedValue = window.localStorage.getItem(commentaryHiddenStorageKey);
            if (!storedValue) return new Set();
            const parsed = JSON.parse(storedValue);
            if (!Array.isArray(parsed)) return new Set();
            return new Set(parsed.map(item => String(item)));
        } catch (_error) {
            return new Set();
        }
    }, [commentaryHiddenStorageKey]);

    const removeCommentaryEntry = useCallback((entryId) => {
        const normalizedId = String(entryId);
        hiddenCommentaryIdsRef.current.add(normalizedId);
        persistHiddenCommentaryIds(hiddenCommentaryIdsRef.current);
        setCommentaryEntries(prev => {
            return prev.filter(entry => String(entry.id) !== normalizedId);
        });
        setCommentarySwipe(prev => (prev.id === entryId ? { id: null, deltaX: 0 } : prev));
        ReactGA.event({ category: 'Commentary', action: 'Entry Dismissed' });
    }, [persistHiddenCommentaryIds]);

    const buildRaceProgressionText = useCallback((sourceRaceId) => {
        const normalizedSourceRaceId = Number(sourceRaceId);
        if (!Number.isFinite(normalizedSourceRaceId) || normalizedSourceRaceId <= 0) {
            return 'Progression: First race';
        }
        const sourceRace = races.find(race => Number(race.raceId) === normalizedSourceRaceId);
        if (!sourceRace) {
            return `Progression: Winner of Race ${normalizedSourceRaceId}`;
        }
        const sourceRaceNo = String(sourceRace.raceNo || normalizedSourceRaceId).trim();
        const sourceWinner = sourceRace.laneA?.winner
            ? sourceRace.laneA
            : sourceRace.laneB?.winner
                ? sourceRace.laneB
                : null;
        if (!sourceWinner) {
            return `Progression: Winner of Race ${sourceRaceNo}`;
        }
        const sourceWinnerLabel = getCrewLabel(sourceWinner, Boolean(sourceRace?.event?.handicapEvent));
        return `Progression: ${sourceWinnerLabel} won Race ${sourceRaceNo}`;
    }, [getCrewLabel, races]);

    const getSeatLabel = useCallback((position, crewSize) => {
        const numericPosition = Number(position);
        const numericCrewSize = Number(crewSize);
        if (!Number.isFinite(numericPosition) || numericPosition <= 0) return '';

        if (numericPosition === 1) return 'B';
        if (numericCrewSize === 9 && numericPosition === 9) return 'C';
        if (numericCrewSize === 8 && numericPosition === 8) return 'S';
        if (numericCrewSize === 5 && numericPosition === 5) return 'C';
        if ((numericCrewSize === 4 || numericCrewSize === 5) && numericPosition === 4) return 'S';
        return String(numericPosition);
    }, []);

    const getRaceDetailNextRoundInfo = useCallback((race) => {
        if (!race) return null;
        const winnerLaneKey = race.laneA?.winner ? 'laneA' : race.laneB?.winner ? 'laneB' : '';
        if (!winnerLaneKey) return null;
        const nextRace = races.find(item => Number(item.raceId) === Number(race.nextRaceId));
        if (!nextRace) {
            return { nextRound: 'Next round: Not available yet', opponent: '' };
        }
        const winnerFeedsLaneA = Number(nextRace.wora) === Number(race.raceId);
        const winnerFeedsLaneB = Number(nextRace.worb) === Number(race.raceId);
        const opponentLane = winnerFeedsLaneA
            ? nextRace.laneB
            : winnerFeedsLaneB
                ? nextRace.laneA
                : null;
        const opponent = opponentLane ? getCrewLabel(opponentLane, Boolean(nextRace?.event?.handicapEvent)) : '';
        const nextRound = `${getRaceBadgeLabel(nextRace.round)} ${getEventCode(nextRace)}`.trim();
        const nextRaceNo = String(nextRace.raceNo || '').trim();
        const nextRoundSummary = `Next round: ${nextRound}${nextRaceNo ? ` (Race ${nextRaceNo})` : ''}`;
        return {
            nextRound: nextRoundSummary,
            opponent: `Opponent: ${opponent && opponent !== 'Unknown crew' ? opponent : 'TBC'}`
        };
    }, [getCrewLabel, getEventCode, getRaceBadgeLabel, races]);

    const closeRaceDetail = useCallback(() => {
        setIsRaceDetailOpen(false);
        setRaceDetailEntry(null);
        setRaceDetailData(null);
        setRaceDetailCrewMembers({ laneA: [], laneB: [] });
        setIsRaceDetailLoading(false);
    }, []);

    const openRaceDetail = useCallback(async (entry) => {
        const raceId = Number(entry?.raceId || 0);
        if (!Number.isFinite(raceId) || raceId <= 0) return;
        const race = races.find(item => Number(item.raceId) === raceId);
        if (!race) return;

        ReactGA.event({ category: 'Commentary', action: 'Race Detail Viewed', label: entry?.kind || 'unknown' });

        setRaceDetailEntry(entry);
        setRaceDetailData(race);
        setRaceDetailCrewMembers({ laneA: [], laneB: [] });
        setIsRaceDetailOpen(true);

        const cached = raceDetailCrewMembersCacheRef.current.get(raceId);
        if (cached) {
            setRaceDetailCrewMembers(cached);
            return;
        }

        setIsRaceDetailLoading(true);
        try {
            const token = getToken();
            const response = await fetch(`/api/commentary/race/${raceId}/crews`, {
                headers: token ? { Authorization: 'Bearer ' + token } : {}
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const payload = await response.json();
            const nextMembers = {
                laneA: Array.isArray(payload?.laneA) ? payload.laneA : [],
                laneB: Array.isArray(payload?.laneB) ? payload.laneB : []
            };
            raceDetailCrewMembersCacheRef.current.set(raceId, nextMembers);
            if (!isMountedRef.current) return;
            setRaceDetailCrewMembers(nextMembers);
        } catch (err) {
            console.error(err);
            if (!isMountedRef.current) return;
            setRaceDetailCrewMembers({ laneA: [], laneB: [] });
        } finally {
            if (!isMountedRef.current) return;
            setIsRaceDetailLoading(false);
        }
    }, [getToken, races]);

    useEffect(() => {
        if (!isRaceDetailOpen || !raceDetailEntry) return;
        const raceId = Number(raceDetailEntry.raceId || 0);
        if (!Number.isFinite(raceId) || raceId <= 0) return;
        const latestRace = races.find(race => Number(race.raceId) === raceId);
        if (latestRace) setRaceDetailData(latestRace);
    }, [isRaceDetailOpen, raceDetailEntry, races]);

    const scheduleCommentarySwipeUpdate = useCallback((nextSwipe) => {
        commentarySwipePendingRef.current = nextSwipe;
        if (commentarySwipeRafRef.current !== null) return;
        commentarySwipeRafRef.current = window.requestAnimationFrame(() => {
            commentarySwipeRafRef.current = null;
            const pending = commentarySwipePendingRef.current;
            setCommentarySwipe(prev => {
                if (prev.id === pending.id && prev.deltaX === pending.deltaX) return prev;
                return pending;
            });
        });
    }, []);

    useEffect(() => {
        const animationTimeouts = commentaryAnimationTimeoutsRef.current;
        return () => {
            if (commentarySwipeRafRef.current !== null) {
                window.cancelAnimationFrame(commentarySwipeRafRef.current);
                commentarySwipeRafRef.current = null;
            }

            animationTimeouts.forEach(timeoutPair => {
                timeoutPair.forEach(timeoutId => window.clearTimeout(timeoutId));
            });
            animationTimeouts.clear();
        };
    }, []);

    const markCommentaryEntriesForAnimation = useCallback((ids) => {
        const normalizedIds = ids.map(id => String(id)).filter(Boolean);
        if (normalizedIds.length === 0) return;

        setCommentaryEntryAnimationStages(prev => {
            const next = { ...prev };
            normalizedIds.forEach(id => {
                next[id] = 'blank';
            });
            return next;
        });

        normalizedIds.forEach(id => {
            const existing = commentaryAnimationTimeoutsRef.current.get(id);
            if (existing) {
                existing.forEach(timeoutId => window.clearTimeout(timeoutId));
            }

            const revealTimeoutId = window.setTimeout(() => {
                setCommentaryEntryAnimationStages(prev => {
                    if (prev[id] !== 'blank') return prev;
                    return { ...prev, [id]: 'reveal' };
                });
            }, 1000);

            const clearTimeoutId = window.setTimeout(() => {
                commentaryAnimationTimeoutsRef.current.delete(id);
                setCommentaryEntryAnimationStages(prev => {
                    if (!(id in prev)) return prev;
                    const next = { ...prev };
                    delete next[id];
                    return next;
                });
            }, 2280);

            commentaryAnimationTimeoutsRef.current.set(id, [revealTimeoutId, clearTimeoutId]);
        });
    }, []);

    const handleCommentaryTouchStart = useCallback((entryId) => (event) => {
        const touch = event.touches?.[0];
        if (!touch) return;
        commentaryTouchRef.current = {
            id: entryId,
            startX: touch.clientX,
            startY: touch.clientY,
            currentX: touch.clientX,
            currentY: touch.clientY,
            swiped: false,
            axisLock: null
        };
    }, []);

    const handleCommentaryTouchMove = useCallback((entryId) => (event) => {
        const touch = event.touches?.[0];
        const state = commentaryTouchRef.current;
        if (!touch || state.id !== entryId) return;

        state.currentX = touch.clientX;
        state.currentY = touch.clientY;
        const deltaX = state.currentX - state.startX;
        const deltaY = Math.abs(state.currentY - state.startY);
        const absDeltaX = Math.abs(deltaX);

        if (!state.axisLock && (absDeltaX > 6 || deltaY > 6)) {
            state.axisLock = absDeltaX > deltaY ? 'x' : 'y';
        }

        if (state.axisLock === 'y') {
            state.swiped = false;
            return;
        }

        if (deltaX < 0 && deltaY < 40) {
            if (event.cancelable) {
                event.preventDefault();
            }
            const clampedDeltaX = Math.max(deltaX, -SWIPE_MAX_DISTANCE);
            scheduleCommentarySwipeUpdate({ id: entryId, deltaX: clampedDeltaX });
        } else if (deltaX >= 0) {
            // Swiped back to the right — cancel any pending delete and reset position
            state.swiped = false;
            scheduleCommentarySwipeUpdate({ id: null, deltaX: 0 });
        }

        state.swiped = deltaX < -SWIPE_DELETE_THRESHOLD && deltaY < 40;
    }, [SWIPE_DELETE_THRESHOLD, SWIPE_MAX_DISTANCE, scheduleCommentarySwipeUpdate]);

    const handleCommentaryTouchEnd = useCallback((entryId) => () => {
        const state = commentaryTouchRef.current;
        if (state.id !== entryId) return;

        if (state.swiped) {
            removeCommentaryEntry(entryId);
        } else {
            setCommentarySwipe(prev => (prev.id === entryId ? { id: null, deltaX: 0 } : prev));
        }

        commentaryTouchRef.current = { id: null, startX: 0, startY: 0, currentX: 0, currentY: 0, swiped: false, axisLock: null };
    }, [removeCommentaryEntry]);

    useEffect(() => {
        hiddenCommentaryIdsRef.current = loadHiddenCommentaryIds();
        setCommentaryEntries(prev => prev.filter(entry => !hiddenCommentaryIdsRef.current.has(String(entry.id))));
    }, [loadHiddenCommentaryIds]);

    const loadCommentaryFeed = useCallback(async () => {
        try {
            const token = getToken();
            const response = await fetch('/api/commentary-feed?limit=300', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const payload = await response.json();
            if (!isMountedRef.current) return;

            const hiddenIds = hiddenCommentaryIdsRef.current;
            const normalized = (Array.isArray(payload) ? payload : [])
                .map((entry, index) => normalizeCommentaryEntry({
                    ...entry,
                    time: entry?.occurredAt ? dayjs(entry.occurredAt).format('HH:mm') : entry?.time,
                    timestamp: entry?.occurredAt ? dayjs(entry.occurredAt).format('HH:mm:ss') : entry?.timestamp,
                    text: entry?.preview || entry?.text || ''
                }, index))
                .filter(Boolean)
                .filter(entry => !hiddenIds.has(String(entry.id)))
                .slice(0, MAX_COMMENTARY_ENTRIES);

            setCommentaryEntries(prevEntries => {
                const prevIds = new Set(prevEntries.map(entry => String(entry.id)));
                const newIds = normalized
                    .map(entry => String(entry.id))
                    .filter(id => !prevIds.has(id));

                markCommentaryEntriesForAnimation(newIds);
                return normalized;
            });
        } catch (err) {
            console.log(err.message);
        }
    }, [MAX_COMMENTARY_ENTRIES, getToken, markCommentaryEntriesForAnimation, normalizeCommentaryEntry]);

    const loadOnCourseTicker = useCallback(async () => {
        try {
            const token = getToken();
            const response = await fetch('/api/race/oncourse', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const payload = await response.json();
            if (!isMountedRef.current) return;

            const items = (Array.isArray(payload) ? payload : [])
                .map(item => String(item?.text || '').trim())
                .filter(Boolean);

            setOnCourseTickerText(items.length ? items.join('  •  ') : 'Course Empty');
        } catch (err) {
            console.log(err.message);
            if (isMountedRef.current) {
                setOnCourseTickerText('Course Empty');
            }
        }
    }, [getToken]);

    const populateRaceData = useCallback(async () => {
        try {
            const token = getToken();
            const response = await fetch('/api/timetable', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const actualData = await response.json();
            if (!isMountedRef.current) return;
            setRaces(actualData);
            setLoading(false);
            const meetingResponse = await fetch('/api/meeting/current', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (meetingResponse.ok) {
                const actualMeeting = await meetingResponse.json();
                if (!isMountedRef.current) return;
                const parsedBreakMinutes = Number(actualMeeting?.divisionBreakMinutes);
                if (Number.isFinite(parsedBreakMinutes) && parsedBreakMinutes > 0) {
                    setDivisionBreakMinutes(parsedBreakMinutes);
                }
                const meetingKey = `commentary-hidden-${actualMeeting?.currentSettingsId || currentYear || 'default'}`;
                setCommentaryHiddenStorageKey(meetingKey);
            }
            if (!isMountedRef.current) return;
            const racesCompleted = actualData.filter(race => race.laneA.winner || race.laneB.winner);
            const racesActual = actualData.filter(race => race.event.id > 0);
            setRacesTG(racesActual.length - racesCompleted.length);
            await loadCommentaryFeed();
            await loadOnCourseTicker();
        } catch (err) {
            console.log(err.message);
        }
    }, [currentYear, getToken, loadCommentaryFeed, loadOnCourseTicker]);

    const calcVL = useCallback(async () => {
        try {
            const token = getToken();
            const response = await fetch('/api/timetable/CalculateVictorLudorum', {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const actualData = await response.json();
            if (!isMountedRef.current) return;
            var out = '';
            for (var i = 0; i < actualData.length; i++) {
                out += actualData[i].shortName + ' ' + actualData[i].points + 'pts; ';
            }
            setVL(out);
        } catch (err) {
            console.log(err.message);
        }
    }, [getToken]);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (authLoading) {
            return undefined;
        }

        populateRaceData();
        calcVL();

        const receiveRaceResultCoalescer = createRealtimeMapCoalescer((events) => {
            if (!isMountedRef.current) return;
            let shouldRecalcVl = false;
            const commentaryBatch = [];

            setRaces(prevRaces => {
                const _races = [...prevRaces];
                let lastRaceUpdate = null;

                events.forEach(({ competitionId, raceid, winnerid, verdict, timetaken, nextrace }) => {
                    let raceUpdate = _races.find(race => race.raceId === raceid && race.competitionId === competitionId);
                    if (!raceUpdate) return;
                    const divisionKey = String(raceUpdate.division || raceUpdate.event?.titleShort || '').trim();
                    const divisionRacesBefore = divisionKey
                        ? prevRaces
                            .filter(race => String(race.division || race.event?.titleShort || '').trim() === divisionKey)
                        : [];
                    const wasDivisionComplete = divisionRacesBefore.length > 0
                        && divisionRacesBefore
                            .map(race => Boolean(race.laneA.winner || race.laneB.winner))
                            .every(Boolean);
                    let winnerInfo;
                    raceUpdate.verdict = verdict;
                    raceUpdate.winningTime = timetaken;
                    raceUpdate.laneA.winner = false;
                    raceUpdate.laneB.winner = false;
                    if (winnerid === 'a') {
                        raceUpdate.laneA.winner = true;
                        winnerInfo = raceUpdate.laneA;
                    }
                    if (winnerid === 'b') {
                        raceUpdate.laneB.winner = true;
                        winnerInfo = raceUpdate.laneB;
                    }
                    if (raceUpdate.laneA.winner || raceUpdate.laneB.winner) {
                        raceUpdate.status = 3;
                        raceUpdate.lastStatusUpdate = new Date().toLocaleString();
                    }
                    const winnerLane = raceUpdate.laneA.winner ? raceUpdate.laneA : raceUpdate.laneB.winner ? raceUpdate.laneB : null;
                    const raceIcon = String(raceUpdate.raceNo || '?');
                    if (winnerLane) {
                        commentaryBatch.push({
                            kind: 'result',
                            raceId: raceUpdate.raceId,
                            round: raceUpdate.round,
                            subject: buildRaceSubject(raceUpdate, 'RESULT'),
                            icon: raceIcon,
                            preview: buildRaceBody(raceUpdate)
                        });

                        const cleanTime = String(raceUpdate.winningTime || '').trim();
                        const currentTimeSeconds = parseRaceTimeSeconds(cleanTime);
                        if (currentTimeSeconds != null) {
                            const fastestTimeSeconds = _races.reduce((min, race) => {
                                const completedLane = race.laneA.winner || race.laneB.winner;
                                if (!completedLane) return min;
                                const parsed = parseRaceTimeSeconds(race.winningTime);
                                if (parsed == null) return min;
                                return Math.min(min, parsed);
                            }, Number.POSITIVE_INFINITY);

                            if (currentTimeSeconds <= fastestTimeSeconds) {
                                commentaryBatch.push({
                                    kind: 'result',
                                    raceId: raceUpdate.raceId,
                                    round: raceUpdate.round,
                                    subject: buildRaceSubject(raceUpdate, 'RESULT'),
                                    icon: raceIcon,
                                    preview: 'That is the fastest time of the day.'
                                });
                            }
                        }
                    }

                    const divisionCompleteAfter = divisionKey
                        ? _races
                            .filter(race => String(race.division || race.event?.titleShort || '').trim() === divisionKey)
                        : [];
                    const isDivisionComplete = divisionCompleteAfter.length > 0
                        && divisionCompleteAfter
                            .map(race => Boolean(race.laneA.winner || race.laneB.winner))
                            .every(Boolean);

                    if (divisionKey && !wasDivisionComplete && isDivisionComplete) {
                        commentaryBatch.push({
                            kind: 'break',
                            raceId: null,
                            subject: 'Between divisions',
                            icon: '⏸',
                            preview: `We now break for ${formatBreakMinutes(divisionBreakMinutes)} between divisions.`
                        });
                    }

                    if ((raceUpdate.round !== 'F' || nextrace?.raceId != null) && winnerInfo) {
                        let nextRound = _races.find(race => race.raceId === nextrace?.raceId);
                        if (nextRound) {
                            if (nextRound.wora === raceUpdate.raceId) {
                                nextRound.laneA.title = winnerInfo.title;
                                nextRound.laneA.titleFull = winnerInfo.titleFull;
                                nextRound.laneA.suffix = winnerInfo.suffix;
                                nextRound.laneA.titleCode = winnerInfo.titleCode;
                                nextRound.laneA.bladeImage = winnerInfo.bladeImage || null;
                                nextRound.laneA.bladeImages = Array.isArray(winnerInfo.bladeImages) ? [...winnerInfo.bladeImages] : (winnerInfo.bladeImage ? [winnerInfo.bladeImage] : []);
                            }
                            if (nextRound.worb === raceUpdate.raceId) {
                                nextRound.laneB.title = winnerInfo.title;
                                nextRound.laneB.titleFull = winnerInfo.titleFull;
                                nextRound.laneB.suffix = winnerInfo.suffix;
                                nextRound.laneB.titleCode = winnerInfo.titleCode;
                                nextRound.laneB.bladeImage = winnerInfo.bladeImage || null;
                                nextRound.laneB.bladeImages = Array.isArray(winnerInfo.bladeImages) ? [...winnerInfo.bladeImages] : (winnerInfo.bladeImage ? [winnerInfo.bladeImage] : []);
                            }
                        }
                    }
                    shouldRecalcVl = true;
                    lastRaceUpdate = raceUpdate;
                });

                if (lastRaceUpdate) {
                    const racesCompleted = _races.filter(race => race.laneA.winner || race.laneB.winner);
                    const racesActual = _races.filter(race => race.event.id > 0);
                    let d = new Date().toLocaleTimeString('en-GB', { hour12: false }) + ' (R' + lastRaceUpdate.raceNo + ' - ' + lastRaceUpdate.raceTimeFormatted + ' - ' + (racesCompleted.length / racesActual.length * 100).toFixed(1) + '%)';
                    setLastUpdate(d);
                    setRacesTG(racesActual.length - racesCompleted.length);
                }

                return _races;
            });

            loadCommentaryFeed();
            loadOnCourseTicker();

            if (shouldRecalcVl) {
                calcVL();
                window.setTimeout(() => {
                    if (!isMountedRef.current) return;
                    calcVL();
                }, 1500);
            }
        }, (event) => `${event.competitionId}:${event.raceid}`);

        const raceStatusUpdatedCoalescer = createRealtimeMapCoalescer((events) => {
            if (!isMountedRef.current) return;
            const commentaryBatch = [];
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceId, statusId }) => {
                    let raceUpdate = _races.find(race => race.raceId === raceId && race.competitionId === competitionId);
                    if (raceUpdate) {
                        raceUpdate.status = statusId;
                        raceUpdate.lastStatusUpdate = new Date().toLocaleString();
                        const raceIcon = String(raceUpdate.raceNo || '?');

                        if (statusId === 1) {
                            commentaryBatch.push({
                                kind: 'next',
                                raceId: raceUpdate.raceId,
                                round: raceUpdate.round,
                                subject: buildRaceSubject(raceUpdate, 'ALIGNING'),
                                icon: raceIcon,
                                preview: buildRaceBody(raceUpdate)
                            });
                        } else if (statusId === 2) {
                            commentaryBatch.push({
                                kind: 'start',
                                raceId: raceUpdate.raceId,
                                round: raceUpdate.round,
                                subject: buildRaceSubject(raceUpdate, 'STARTED'),
                                icon: raceIcon,
                                preview: buildRaceBody(raceUpdate)
                            });
                        } else if (statusId === 0) {
                            commentaryBatch.push({
                                kind: 'status',
                                raceId: raceUpdate.raceId,
                                round: raceUpdate.round,
                                subject: buildRaceSubject(raceUpdate, 'STOPPED'),
                                icon: raceIcon,
                                preview: buildRaceBody(raceUpdate)
                            });
                        }
                    }
                });
                return _races;
            });
            loadCommentaryFeed();
            loadOnCourseTicker();
        }, (event) => `${event.competitionId}:${event.raceId}`);

        const raceMovedCoalescer = createRealtimeMapCoalescer((events) => {
            if (!isMountedRef.current) return;
            setRaces(prevRaces => {
                const _races = [...prevRaces];
                events.forEach(({ competitionId, raceid, newraceid }) => {
                    if (_races.length === 0 || _races[0].competitionId !== competitionId) return;
                    const newRaceId = parseInt(newraceid, 10);
                    const raceA = Object.assign({}, _races.find(race => race.raceId === raceid));
                    const raceB = Object.assign({}, _races.find(race => race.raceId === newRaceId));
                    let destinationRace = _races.find(race => race.raceId === newRaceId);
                    let sourceRace = _races.find(race => race.raceId === raceid);
                    if (!destinationRace || !sourceRace || !raceA || !raceB) return;
                    destinationRace.raceNo = raceA.raceNo;
                    destinationRace.laneA.title = raceA.laneA.title;
                    destinationRace.laneA.titleShort = raceA.laneA.titleShort;
                    destinationRace.laneA.suffix = raceA.laneA.suffix;
                    destinationRace.laneA.bladeImage = raceA.laneA.bladeImage || null;
                    destinationRace.laneA.bladeImages = Array.isArray(raceA.laneA.bladeImages) ? [...raceA.laneA.bladeImages] : (raceA.laneA.bladeImage ? [raceA.laneA.bladeImage] : []);
                    destinationRace.laneB.title = raceA.laneB.title;
                    destinationRace.laneB.titleShort = raceA.laneB.titleShort;
                    destinationRace.laneB.suffix = raceA.laneB.suffix;
                    destinationRace.laneB.bladeImage = raceA.laneB.bladeImage || null;
                    destinationRace.laneB.bladeImages = Array.isArray(raceA.laneB.bladeImages) ? [...raceA.laneB.bladeImages] : (raceA.laneB.bladeImage ? [raceA.laneB.bladeImage] : []);
                    destinationRace.event.id = raceA.event.id;
                    destinationRace.event.title = raceA.event.title;
                    destinationRace.event.titleShort = raceA.event.titleShort;
                    destinationRace.round = raceA.round;
                    destinationRace.divsion = raceA.division;
                    destinationRace.nextRaceId = raceA.nextRaceId;
                    sourceRace.raceNo = raceB.raceNo;
                    sourceRace.laneA.title = '';
                    sourceRace.laneB.title = '';
                    sourceRace.laneA.suffix = '';
                    sourceRace.laneB.suffix = '';
                    sourceRace.laneA.bladeImage = null;
                    sourceRace.laneA.bladeImages = [];
                    sourceRace.laneB.bladeImage = null;
                    sourceRace.laneB.bladeImages = [];
                    sourceRace.event.id = -1;
                    sourceRace.event.title = '';
                    sourceRace.event.titleShort = '';
                    sourceRace.round = '';
                    sourceRace.nextRaceId = '';
                });
                return _races;
            });
        }, (event) => `${event.competitionId}:${event.raceid}`);

        const unsubscribe = subscribeToRealtime({
            onStatusChange: (nextStatus, updateTime) => {
                if (!isMountedRef.current) return;
                setStatus(nextStatus);
                setLastUpdate(updateTime);
                if (isAdmin && (nextStatus === 'Connected!' || nextStatus === 'Reconnected!')) {
                    getConnectedUsersCount().then(count => setConnectedUsers(count));
                }
            },
            onReconnected: () => {
                if (!isMountedRef.current) return;
                populateRaceData();
                calcVL();
                loadOnCourseTicker();
            },
            onConnectedUsersCountUpdated: (count) => {
                if (!isMountedRef.current) return;
                if (!isAdmin) return;
                const parsed = Number(count);
                setConnectedUsers(Number.isFinite(parsed) ? parsed : null);
            },
            onReceiveRaceResult: (competitionId, raceid, winnerid, verdict, timetaken, nextrace) => {
                receiveRaceResultCoalescer.push({ competitionId, raceid, winnerid, verdict, timetaken, nextrace });
            },
            onRaceStatusUpdated: (competitionId, raceId, statusId) => {
                raceStatusUpdatedCoalescer.push({ competitionId, raceId, statusId });
            },
            onRaceMoved: (competitionId, raceid, newraceid) => {
                raceMovedCoalescer.push({ competitionId, raceid, newraceid });
            }
        });
        return () => {
            receiveRaceResultCoalescer.dispose();
            raceStatusUpdatedCoalescer.dispose();
            raceMovedCoalescer.dispose();
            unsubscribe();
        };
    }, [authLoading, buildRaceBody, buildRaceSubject, calcVL, divisionBreakMinutes, formatBreakMinutes, loadCommentaryFeed, loadOnCourseTicker, parseRaceTimeSeconds, populateRaceData, isAdmin]);

    useEffect(() => {
        if (!isAdmin) {
            setConnectedUsers(null);
            return undefined;
        }

        let mounted = true;
        const loadCount = async () => {
            const count = await getConnectedUsersCount();
            if (mounted) setConnectedUsers(count);
        };

        loadCount();
        const intervalId = window.setInterval(loadCount, 15000);

        return () => {
            mounted = false;
            window.clearInterval(intervalId);
        };
    }, [isAdmin]);

    const renderCommentaryFeed = () => {
        return (
            <div className="commentary-feed-body">
                    {commentaryEntries.length === 0 ? (
                        <div className="commentary-feed-empty text-muted">Waiting for live updates...</div>
                    ) : (
                        <ListGroup variant="flush" className="commentary-feed-list">
                            {commentaryEntries.map((entry, index) => {
                                const parsedFromText = parseRoundAndEventFromText(entry.preview || entry.text || '');
                                const effectiveRound = String(entry.round || parsedFromText.round || '').trim();
                                const effectiveRaceNo = String(entry.raceNo || parsedFromText.raceNo || '').trim();
                                const animationStage = commentaryEntryAnimationStages[String(entry.id)] || '';
                                const displayIcon = entry.kind === 'break'
                                    ? (entry.icon || '⏸')
                                    : (String(entry.icon || effectiveRaceNo).trim() || getRaceBadgeLabel(effectiveRound));
                                const displaySubject = String(entry.subject || '').trim() || [effectiveRound, parsedFromText.eventCode || '', parsedFromText.status || ''].filter(Boolean).join(' ').trim();
                                const displayResultLine = String(entry.resultLine || '').trim() || String(entry.preview || entry.text || '').trim();
                                const displayWinningTime = String(entry.winningTime || '').trim();
                                const displayVerdict = String(entry.verdict || '').trim();
                                const showFastestMedal = Boolean(entry.isEventFastest) && Number(entry.eventResultCount || 0) > 1;
                                const hasRaceDetail = Number(entry.raceId || 0) > 0 && entry.kind !== 'break';
                                const raceForEntry = hasRaceDetail
                                    ? races.find(race => Number(race.raceId) === Number(entry.raceId))
                                    : null;

                                return (
                                <div key={entry.id || `${entry.time}-${index}-${entry.kind}`} className={`commentary-feed-swipe-shell commentary-feed-item-${entry.kind} ${animationStage === 'blank' ? 'is-new-entry-blank' : ''} ${animationStage === 'reveal' ? 'is-new-entry-reveal' : ''}`}>
                                    <div className="commentary-feed-delete-action" aria-hidden="true">
                                        <span>Delete</span>
                                    </div>
                                    <ListGroup.Item
                                        className={`commentary-feed-item commentary-feed-item-${entry.kind} commentary-feed-status-${entry.statusTone || 'neutral'} ${commentarySwipe.id === entry.id ? 'is-swipe-active' : ''} ${hasRaceDetail ? 'commentary-feed-item-interactive' : ''}`}
                                        style={{ transform: commentarySwipe.id === entry.id ? `translateX(${commentarySwipe.deltaX}px)` : 'translateX(0)' }}
                                        onTouchStart={handleCommentaryTouchStart(entry.id)}
                                        onTouchMove={handleCommentaryTouchMove(entry.id)}
                                        onTouchEnd={handleCommentaryTouchEnd(entry.id)}
                                        onTouchCancel={handleCommentaryTouchEnd(entry.id)}
                                        onClick={hasRaceDetail ? () => openRaceDetail(entry) : undefined}
                                        role={hasRaceDetail ? 'button' : undefined}
                                        tabIndex={hasRaceDetail ? 0 : undefined}
                                        onKeyDown={hasRaceDetail ? (event) => {
                                            if (event.key === ' ' || event.key === 'Enter') {
                                                event.preventDefault();
                                                openRaceDetail(entry);
                                            }
                                        } : undefined}
                                    >
                                        {animationStage === 'blank' ? (
                                            <div className="commentary-feed-mail-row commentary-feed-mail-row-placeholder" aria-hidden="true">
                                                <div className="commentary-feed-mail-icon commentary-feed-mail-icon-placeholder" />
                                                <div className="commentary-feed-mail-body commentary-feed-mail-body-placeholder">
                                                    <div className="commentary-feed-placeholder-line commentary-feed-placeholder-subject" />
                                                    <div className="commentary-feed-placeholder-line commentary-feed-placeholder-preview" />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="commentary-feed-mail-row">
                                                <div className="commentary-feed-mail-icon" aria-hidden="true">{displayIcon || 'R?'}</div>
                                                <div className="commentary-feed-mail-body">
                                                    <div className="commentary-feed-mail-top">
                                                        <div className="commentary-feed-mail-subject">{displaySubject}</div>
                                                        <div className="commentary-feed-mail-time">{entry.time}</div>
                                                    </div>
                                                    {entry.kind === 'result' ? (
                                                        <>
                                                            <div className="commentary-feed-mail-preview">{displayResultLine}</div>
                                                            <div className="commentary-feed-result-divider" aria-hidden="true" />
                                                            <div className="commentary-feed-result-meta">
                                                                <span>
                                                                    {'Time Taken: '}
                                                                    {displayWinningTime || '-'}
                                                                    {showFastestMedal && <span className="commentary-feed-result-medal" title="Fastest time in event">🏅</span>}
                                                                    {' Verdict: '}
                                                                    {displayVerdict || '-'}
                                                                </span>
                                                            </div>
                                                        </>
                                                    ) : (
                                                        raceForEntry ? (
                                                            <div className="commentary-feed-race-preview">
                                                                <span>{getCrewLabelWithBlades(raceForEntry.laneA, Boolean(raceForEntry?.event?.handicapEvent))}</span>
                                                                <span className="commentary-feed-race-preview-sep">v.</span>
                                                                <span>{getCrewLabelWithBlades(raceForEntry.laneB, Boolean(raceForEntry?.event?.handicapEvent))}</span>
                                                            </div>
                                                        ) : (
                                                            <div className="commentary-feed-mail-preview">{entry.preview || entry.text}</div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </ListGroup.Item>
                                </div>
                            )})}
                        </ListGroup>
                    )}
                </div>
        );
    };

    const tickerDisplayText = onCourseTickerText === 'Course Empty'
        ? 'Course Empty'
        : `On Course Now: ${onCourseTickerText}`;

    let contents = loading ? <p><em>Loading...</em></p> : renderCommentaryFeed();
    const detailRace = raceDetailData;
    const detailEntry = raceDetailEntry;
    const detailIsResult = detailEntry?.kind === 'result';
    const detailWinnerLaneKey = detailRace?.laneA?.winner ? 'laneA' : detailRace?.laneB?.winner ? 'laneB' : '';
    const detailWinnerFirstRows = detailIsResult && detailWinnerLaneKey
        ? [
            {
                key: detailWinnerLaneKey,
                heading: 'Winner',
                laneHeading: '',
                laneData: detailRace[detailWinnerLaneKey],
                members: detailWinnerLaneKey === 'laneA' ? raceDetailCrewMembers.laneA : raceDetailCrewMembers.laneB,
                progressionSourceRaceId: detailWinnerLaneKey === 'laneA' ? detailRace.wora : detailRace.worb
            },
            {
                key: detailWinnerLaneKey === 'laneA' ? 'laneB' : 'laneA',
                heading: 'Loser',
                laneHeading: '',
                laneData: detailRace[detailWinnerLaneKey === 'laneA' ? 'laneB' : 'laneA'],
                members: detailWinnerLaneKey === 'laneA' ? raceDetailCrewMembers.laneB : raceDetailCrewMembers.laneA,
                progressionSourceRaceId: detailWinnerLaneKey === 'laneA' ? detailRace.worb : detailRace.wora
            }
        ]
        : detailRace
            ? [
                {
                    key: 'laneA',
                    heading: 'Lane A',
                    laneHeading: 'Lane A',
                    laneData: detailRace.laneA,
                    members: raceDetailCrewMembers.laneA,
                    progressionSourceRaceId: detailRace.wora
                },
                {
                    key: 'laneB',
                    heading: 'Lane B',
                    laneHeading: 'Lane B',
                    laneData: detailRace.laneB,
                    members: raceDetailCrewMembers.laneB,
                    progressionSourceRaceId: detailRace.worb
                }
            ]
            : [];
    const detailNextRound = detailIsResult ? getRaceDetailNextRoundInfo(detailRace) : null;

    return (
        <div className="pb-5 mb-5">
            <div className='container-fluid'>
                <div className='row'>
                    <div className='col-10 p-1'><h1 id="tabelLabel" className="noprint">Commentary View</h1>
                        <h1 className="noscreen">Commentary</h1></div>
                    <div className='col-2'></div>
                </div>
            </div>
            <div className='container-fluid'>
                <div className='row'>
                    <div className='col-12 mb-2 fs-6' style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><b>VL</b> <small>{racesTG }TG</small> :&nbsp;
                        { vl && vl }</div>
                </div>
                <div className='row'><hr/></div>
            </div>
            {contents}
            <Modal show={isRaceDetailOpen} onHide={closeRaceDetail} fullscreen="sm-down" centered dialogClassName="commentary-race-detail-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        Race {detailRace?.raceNo || '?'} details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {detailRace ? (
                        <>
                            <div className="commentary-race-detail-subtitle">
                                {`${getRaceBadgeLabel(detailRace?.round)} ${getEventCode(detailRace)}`.trim()}
                            </div>
                            {detailNextRound && (
                                <div className="commentary-race-detail-next-round">
                                    <div>{detailNextRound.nextRound}</div>
                                    {detailNextRound.opponent && <div>{detailNextRound.opponent}</div>}
                                </div>
                            )}
                            {detailWinnerFirstRows.map((row) => (
                                <section className="commentary-race-detail-lane" key={row.key}>
                                    <div className="commentary-race-detail-lane-header">
                                        <span className="commentary-race-detail-lane-label">{row.heading || row.laneHeading}</span>
                                        <span className="commentary-race-detail-lane-name">{getCrewLabelWithBlades(row.laneData, Boolean(detailRace?.event?.handicapEvent))}</span>
                                    </div>
                                    {!detailIsResult && (
                                        <div className="commentary-race-detail-progression">
                                            {buildRaceProgressionText(row.progressionSourceRaceId)}
                                        </div>
                                    )}
                                    <div className="commentary-race-detail-members-title">Crew members</div>
                                    {isRaceDetailLoading ? (
                                        <div className="commentary-race-detail-members-empty">Loading crew members...</div>
                                    ) : row.members.length === 0 ? (
                                        <div className="commentary-race-detail-members-empty">No crew member details available.</div>
                                    ) : (
                                        <ul className="commentary-race-detail-members-list">
                                            {row.members.map((member, index) => {
                                                const name = [
                                                    String(member?.firstName || '').trim(),
                                                    String(member?.lastName || '').trim()
                                                ].filter(Boolean).join(' ');
                                                const position = Number(member?.position);
                                                const fallbackPosition = index + 1;
                                                const seatLabel = getSeatLabel(Number.isFinite(position) && position > 0 ? position : fallbackPosition, row.members.length);
                                                return (
                                                    <li key={`${row.key}-${member?.position || 'p'}-${index}`}>
                                                        <span className="commentary-race-detail-member-seat">{seatLabel}</span>
                                                        <span>{name || 'Unnamed crew member'}</span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </section>
                            ))}
                        </>
                    ) : (
                        <div className="commentary-race-detail-members-empty">Race details are not available.</div>
                    )}
                </Modal.Body>
            </Modal>
            <div className="fixed-bottom">
                <Navbar bg="light" variant="light">
                    <Container>
                        <NavbarBrand></NavbarBrand>
                        <Navbar.Text className='courseTickerWrap'>
                            <div className='courseTickerTrack'>
                                <span className={`courseTickerText ${onCourseTickerText === 'Course Empty' ? 'is-empty' : ''}`}>{tickerDisplayText}</span>
                            </div>
                        </Navbar.Text>
                    </Container>
                </Navbar>
            </div>
            <div className="divFooter container-fluid">
                <div className='row'>
                    <div className='col-9'></div>
                    <div className='col-3'></div>
                </div>
            </div>
        </div>
    );
}
